import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { debounce, isUndefined, pick, uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AppliedFiltersChips, {
  AppliedFiltersChipsCollectedData,
} from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, {
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import useFetchers, {
  FetchDriversFunction,
  FetchFunction,
} from '~components/Filters/useFetchers';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { LiveMapSelectedFilter } from '~constants/enums';
import useFilterCategoryLabels from '~hooks/useFilterCategoryLabels';
import useLiveMapFilters, {
  DriversRequestCursors,
  TypeaheadedFilterState,
} from '~hooks/useLiveMap/useLiveMapFilters';
import { ordersDispatchFilterKeyBySelectedFilter } from '~hooks/useLiveMap/useLiveMapFilters';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LiveMapDateFilter from './LiveMapDateFilter';

export type NextPageCursorsState = Partial<
  Record<LiveMapSelectedFilter, TypeaheadedFilterState['cursor']>
>;

const FILTERS_DEBOUNCE_DELAY_IN_MS = 300;
const noneOptionFilters = [LiveMapSelectedFilter.DEPARTMENTS];

const LiveMapFilters = observer(() => {
  const { allEntitiesLabel, categoryLabel, noEntitiesLabel } = useFilterCategoryLabels();
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchor);
  const [selectedFilter, setSelectedFilter] = useState<LiveMapSelectedFilter>();

  const {
    appliedFilters,
    updateAppliedFilters,
    availableOptions,
    updateAvailableOptions,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  } = useLiveMapFilters();

  const isSiteTypesFilterSelected = selectedFilter === LiveMapSelectedFilter.SITE_TYPES;

  const { userStore, liveMapStoreNew } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [nextPageCursors, setNextPageCursors] = useState<NextPageCursorsState>({});
  const fetchers = useFetchers(companyId);

  const fetchersBySelectedFilter: Partial<
    Record<LiveMapSelectedFilter, FetchFunction | FetchDriversFunction>
  > = useMemo(
    () => ({
      [LiveMapSelectedFilter.CUSTOMERS]: fetchers.fetchCustomers,
      [LiveMapSelectedFilter.DEPARTMENTS]: fetchers.fetchDepartments,
      [LiveMapSelectedFilter.DISPATCH_NUMBERS]: fetchers.fetchOrdersDispatchNumbers,
      [LiveMapSelectedFilter.DRIVERS]: fetchers.fetchDrivers,
      [LiveMapSelectedFilter.DROP_OFF_SITES]: fetchers.fetchSites,
      [LiveMapSelectedFilter.PICK_UP_SITES]: fetchers.fetchSites,
      [LiveMapSelectedFilter.PROJECTS]: fetchers.fetchProjects,
      [LiveMapSelectedFilter.PROJECTS_EXTERNAL_IDS]: fetchers.fetchProjectExternalIds,
      [LiveMapSelectedFilter.REQUESTERS]: fetchers.fetchRequesters,
      [LiveMapSelectedFilter.VENDORS]: fetchers.fetchVendors,
    }),
    [fetchers],
  );

  const filterChipsData = useMemo(() => {
    const appliedFilters = pick(
      liveMapStoreNew.filters,
      Object.values(ordersDispatchFilterKeyBySelectedFilter),
    );

    const collectedData = Object.entries(appliedFilterChipsCollectedData).reduce(
      (obj, [key, collectedValues]) => {
        const filter =
          ordersDispatchFilterKeyBySelectedFilter[key as LiveMapSelectedFilter];

        obj[filter] = collectedValues;

        return obj;
      },
      {} as AppliedFiltersChipsCollectedData,
    );

    return { appliedFilters, collectedData };
  }, [JSON.stringify(liveMapStoreNew.filters), appliedFilterChipsCollectedData]);

  const hasFiltersApplied =
    Object.values(filterChipsData.appliedFilters).filter((value) => {
      return Boolean(value && value.length > 0);
    }).length > 0;

  const applyDebouncedFilter = debounce((callback: () => void) => {
    callback();
  }, FILTERS_DEBOUNCE_DELAY_IN_MS);

  const prepareForNewPageRequest = (key: LiveMapSelectedFilter) => {
    const isDriversFilter = key === LiveMapSelectedFilter.DRIVERS;
    const hasAnyDriversNextPageCursors =
      'sharedDriversCursor' in availableOptions.drivers ||
      'nonSharedDriversCursor' in availableOptions.drivers;
    const allDriversPagesFetched =
      isUndefined(availableOptions.drivers.sharedDriversCursor) &&
      isUndefined(availableOptions.drivers.nonSharedDriversCursor);

    // We know that all records has been fetched if the cursor is explictly "undefined"
    const hasNextPageCursor = isDriversFilter
      ? hasAnyDriversNextPageCursors
      : 'cursor' in availableOptions[key];

    if (!hasNextPageCursor) {
      return;
    }

    const allPagesFetched = isDriversFilter
      ? allDriversPagesFetched
      : isUndefined(availableOptions[key].cursor);

    if (allPagesFetched) {
      return;
    }

    updateAvailableOptions((state) => ({
      ...state,
      [key]: {
        ...state[key],
        loading: FilterMenuItemLoadingReason.INFINITE_SCROLL,
      },
    }));
  };

  const applySearchResponse = (key: LiveMapSelectedFilter, data: FilterMenuOption[]) => {
    updateAvailableOptions((state) => ({
      ...state,
      [key]: {
        ...state[key],
        loading: false,
        options: uniqBy([...state[key].options, ...data], (i) => i.value),
      },
    }));

    updateAppliedFilterChipsCollectedData((state) => ({
      ...state,
      [key]: uniqBy([...(state[key] ?? []), ...data], (i) => i.value),
    }));
  };

  const getFetchMoreCallback = useCallback(() => {
    if (!selectedFilter) {
      return undefined;
    }

    const isDriversFilter = selectedFilter === LiveMapSelectedFilter.DRIVERS;

    if (!isDriversFilter) {
      const allPagesFetched =
        'cursor' in availableOptions[selectedFilter] &&
        isUndefined(availableOptions[selectedFilter].cursor);

      if (allPagesFetched) {
        return undefined;
      }

      return () => {
        updateAvailableOptions((state) => ({
          ...state,
          [selectedFilter]: {
            ...state[selectedFilter],
            cursor: nextPageCursors[selectedFilter],
          },
        }));
      };
    }

    const allPagesFetched =
      'sharedDriversCursor' in availableOptions.drivers &&
      isUndefined(availableOptions.drivers.sharedDriversCursor) &&
      'nonSharedDriversCursor' in availableOptions.drivers &&
      isUndefined(availableOptions.drivers.nonSharedDriversCursor);

    if (allPagesFetched) {
      return undefined;
    }

    return () => {
      if (nextPageCursors?.drivers) {
        updateAvailableOptions((state) => ({
          ...state,
          drivers: {
            ...state.drivers,
            sharedDriversCursor: (nextPageCursors.drivers as DriversRequestCursors)
              .sharedDriversCursor,
            nonSharedDriversCursor: (nextPageCursors.drivers as DriversRequestCursors)
              .nonSharedDriversCursor,
          },
        }));
      }
    };
  }, [selectedFilter, availableOptions]);

  const getNoneOptionLabel = (selectedFilter: LiveMapSelectedFilter) => {
    if (noneOptionFilters.includes(selectedFilter)) {
      return noEntitiesLabel(selectedFilter);
    }
  };

  const fetch = (filter: LiveMapSelectedFilter) => {
    const fetcher = fetchersBySelectedFilter[filter];

    if (fetcher && availableOptions[filter].loading) {
      if (filter === LiveMapSelectedFilter.DRIVERS) {
        const { searchValue, sharedDriversCursor, nonSharedDriversCursor } =
          availableOptions[filter];

        (fetcher as FetchDriversFunction)(
          searchValue,
          sharedDriversCursor,
          nonSharedDriversCursor,
        ).then((response) => {
          setNextPageCursors((state) => ({
            ...state,
            [filter]: {
              sharedDriversCursor: response.nextSharedDriversPageCursor,
              nonSharedDriversCursor: response.nextNonSharedDriversPageCursor,
            },
          }));
          applySearchResponse(filter, response.data);
        });
      } else {
        const { searchValue, cursor } = availableOptions[filter];

        (fetcher as FetchFunction)(searchValue, cursor as string).then((response) => {
          setNextPageCursors((state) => ({
            ...state,
            [filter]: response.nextPageCursor,
          }));
          applySearchResponse(filter, response.data);
        });
      }
    }
  };

  useEffect(() => {
    fetch(LiveMapSelectedFilter.CUSTOMERS);
  }, [availableOptions.customers.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.DISPATCH_NUMBERS);
  }, [availableOptions.dispatchNumbers.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.DROP_OFF_SITES);
  }, [availableOptions.dropOffSites.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.DRIVERS);
  }, [availableOptions.drivers.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.PICK_UP_SITES);
  }, [availableOptions.pickUpSites.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.PROJECTS);
  }, [availableOptions.projects.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.PROJECTS_EXTERNAL_IDS);
  }, [availableOptions.projectsExternalIds.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.REQUESTERS);
  }, [availableOptions.requesters.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.VENDORS);
  }, [availableOptions.vendors.loading]);

  useEffect(() => {
    fetch(LiveMapSelectedFilter.DEPARTMENTS);
  }, [availableOptions.departments.loading]);

  useEffect(() => {
    if (selectedFilter) {
      prepareForNewPageRequest(selectedFilter);
    }
  }, [
    selectedFilter,
    availableOptions.customers.cursor,
    availableOptions.dispatchNumbers.cursor,
    availableOptions.drivers.nonSharedDriversCursor,
    availableOptions.drivers.sharedDriversCursor,
    availableOptions.dropOffSites.cursor,
    availableOptions.pickUpSites.cursor,
    availableOptions.projects.cursor,
    availableOptions.projectsExternalIds.cursor,
    availableOptions.requesters.cursor,
    availableOptions.vendors.cursor,
    availableOptions.departments.cursor,
  ]);

  useEffect(() => {
    if (selectedFilter && !isSiteTypesFilterSelected) {
      updateAvailableOptions((state) => ({
        ...state,
        [selectedFilter]: {
          loading: false,
          options: [],
          searchValue: state[selectedFilter].searchValue,
        },
      }));

      applyDebouncedFilter(() => {
        updateAvailableOptions((state) => ({
          ...state,
          [selectedFilter]: {
            loading: FilterMenuItemLoadingReason.SEARCH_VALUE,
            options: [],
            searchValue: appliedFilters[selectedFilter].search,
          },
        }));
      });
    }

    return () => {
      applyDebouncedFilter.cancel();
    };
  }, [
    selectedFilter,
    isSiteTypesFilterSelected,
    appliedFilters.customers.search,
    appliedFilters.dispatchNumbers.search,
    appliedFilters.drivers.search,
    appliedFilters.dropOffSites.search,
    appliedFilters.pickUpSites.search,
    appliedFilters.projects.search,
    appliedFilters.projectsExternalIds.search,
    appliedFilters.requesters.search,
    appliedFilters.vendors.search,
    appliedFilters.departments.search,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
    }
  }, [isOpen]);

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => [filter, undefined]),
    );

    liveMapStoreNew.setFilters(filtersObject, true);
  }, []);

  const handleRemoveOneFilter = useCallback((filter: string) => {
    liveMapStoreNew.setFilters({ [filter]: undefined }, true);
  }, []);

  return (
    <>
      <Box sx={{ gridArea: 'dateSelector' }}>
        <LiveMapDateFilter />
      </Box>
      <Box sx={{ gridArea: 'filters', display: 'flex' }}>
        <SmallButton
          onClick={(e) => setAnchor(e.currentTarget)}
          size="small"
          startIcon={<FilterList />}
          sx={{
            '&.MuiButton-root': {
              backgroundColor: theme.semantic.surfaceRaised,
              color: theme.palette.text.primary,
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              ...(hasFiltersApplied
                ? {
                    borderBottomLeftRadius: theme.brandV2.borderRadius,
                    borderBottomRightRadius: 0,
                    borderTopLeftRadius: theme.brandV2.borderRadius,
                    borderTopRightRadius: 0,
                  }
                : { borderRadius: theme.brandV2.borderRadius }),
            },
            '& .MuiButton-startIcon': {
              mr: '-2px',
            },
          }}
        />

        <Menu
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          open={isOpen}
          sx={{
            '& .MuiPaper-root': {
              border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              boxShadow: '0px 1px 10px 0px rgba(60, 64, 67, 0.15)',
              mt: '3px',
              width: '250px',
            },
            '& .MuiItem-root': {
              px: theme.spacing(1.5),
            },
          }}
        >
          {selectedFilter && (
            <FilterMenuItem
              focusSearchFieldOnMount
              searchValue={appliedFilters[selectedFilter].search}
              onSearchValueChange={
                isSiteTypesFilterSelected
                  ? undefined
                  : (value) => {
                      updateAppliedFilters((state) => ({
                        ...state,
                        [selectedFilter]: {
                          ...state[selectedFilter],
                          search: value,
                        },
                      }));
                    }
              }
              loadingReason={availableOptions[selectedFilter].loading || undefined}
              selectAllOptionLabel={allEntitiesLabel(selectedFilter)}
              options={availableOptions[selectedFilter].options}
              selectedOptions={
                liveMapStoreNew.filters[
                  ordersDispatchFilterKeyBySelectedFilter[selectedFilter]
                ] ?? []
              }
              onSelectedOptionsChange={(selectedOptions) => {
                liveMapStoreNew.setFilters(
                  {
                    [ordersDispatchFilterKeyBySelectedFilter[selectedFilter]]:
                      selectedOptions.length > 0 ? selectedOptions : undefined,
                  },
                  true,
                );
              }}
              onFetchMore={getFetchMoreCallback()}
              selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
            />
          )}

          {!selectedFilter &&
            Object.values(LiveMapSelectedFilter).map((filter) => (
              <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                    {categoryLabel(filter)}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {(
                      liveMapStoreNew.filters[
                        ordersDispatchFilterKeyBySelectedFilter[filter]
                      ] ?? []
                    ).length > 0 && (
                      <Check
                        sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                      />
                    )}

                    <ChevronRight sx={{ fontSize: '16px' }} />
                  </Box>
                </Box>
              </MenuItem>
            ))}
        </Menu>

        <AppliedFiltersChips
          {...filterChipsData}
          onRemoveAllFilters={handleRemoveAllFilters}
          onRemoveFilter={handleRemoveOneFilter}
          maxVisibleFilters={0}
          sx={{
            ml: -1,
          }}
        />
      </Box>
    </>
  );
});

export default LiveMapFilters;
